import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';
import { categoryIcon, categoryColor } from '../utils/categoryStyle';
import TopSpendersView from './TopSpendersView';
import BigPurchasesView from './BigPurchasesView';

const PAGE_SIZE = 50;

function getDateRangeForPreset(preset) {
  const now = new Date();
  const iso = (d) => d.toISOString().split('T')[0];

  switch (preset) {
    case 'this_week': {
      // Sunday-start week, matching the backend's getWeekStart()
      const day = now.getDay();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
      return { startDate: iso(start) };
    }
    case 'this_month':
      return { startDate: iso(new Date(now.getFullYear(), now.getMonth(), 1)) };
    case 'last_month': {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { startDate: iso(start), endDate: iso(end) };
    }
    case 'last_3_months':
      return { startDate: iso(new Date(now.getFullYear(), now.getMonth() - 3, 1)) };
    case 'this_year':
      return { startDate: iso(new Date(now.getFullYear(), 0, 1)) };
    case 'all':
    default:
      return {};
  }
}

const PRESETS = [
  { id: 'this_week', label: 'This Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'last_3_months', label: 'Last 3 Months' },
  { id: 'this_year', label: 'This Year' },
  { id: 'all', label: 'All Time' },
];

const MODES = [
  { id: 'list', label: 'List' },
  { id: 'recap', label: 'Monthly Recap' },
  { id: 'top', label: '🔍 Top Spenders' },
  { id: 'big', label: '💥 Big Purchases' },
  { id: 'income', label: '💰 Income' },
  { id: 'transfers', label: '🔄 Transfers' },
];

const currentMonthValue = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export default function AllTransactions({ onGoToManage, initialSearch = '', initialCategory = '', initialPreset = 'this_month', initialMode = 'list', onRefresh, bigPurchaseThreshold, bigPurchaseExcludedCategories, refreshKey }) {
  const [mode, setMode] = useState(initialMode);
  const [preset, setPreset] = useState(initialPreset); // List & Recap
  const [search, setSearch] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [category, setCategory] = useState(initialCategory); // List only - set via a category click-through, clearable
  const [filterCategories, setFilterCategories] = useState([]); // options for the category filter dropdown
  const [accountId, setAccountId] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [sortBy, setSortBy] = useState('date'); // List only
  const [recapView, setRecapView] = useState('merchant'); // which breakdown to show when a month is expanded

  // List
  const [page, setPage] = useState(0);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // Recap - collapsible list of the last 12 months
  const [recapMonths, setRecapMonths] = useState(null);
  const [loadingRecapMonths, setLoadingRecapMonths] = useState(false);
  const [expandedRecapMonth, setExpandedRecapMonth] = useState(null);
  const [recapDetail, setRecapDetail] = useState({}); // month -> { byMerchant, byCategory, ... }
  const [loadingRecapDetail, setLoadingRecapDetail] = useState(null); // which month is loading

  // Income / Transfers share the same period shape
  const [periodType, setPeriodType] = useState('year'); // 'year' | 'month'
  const [selectedMonth, setSelectedMonth] = useState(currentMonthValue());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [incomeData, setIncomeData] = useState(null);
  const [loadingIncome, setLoadingIncome] = useState(false);
  const [transfersData, setTransfersData] = useState(null);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    api.getAccounts().then(setAccounts).catch(() => setAccounts([]));
  }, []);

  useEffect(() => {
    api.getCategoriesList().then(setFilterCategories).catch(() => setFilterCategories([]));
  }, []);

  // ─── List ──────────────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const range = getDateRangeForPreset(preset);
      const data = await api.getTransactionsList({
        ...range,
        merchant: debouncedSearch || undefined,
        category: category || undefined,
        sortBy,
        accountId: accountId || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      });
      setResult(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [preset, debouncedSearch, category, sortBy, accountId, page]);

  useEffect(() => { if (mode === 'list') loadList(); }, [mode, loadList]);
  useEffect(() => { setPage(0); }, [preset, debouncedSearch, category, sortBy, accountId]);

  // ─── Recap ─────────────────────────────────────────────────────────────
  const loadRecapMonths = useCallback(async () => {
    setLoadingRecapMonths(true);
    try {
      const data = await api.getRecapMonths({
        accountId: accountId || undefined,
        merchant: debouncedSearch || undefined,
      });
      setRecapMonths(data.months);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingRecapMonths(false);
    }
  }, [accountId, debouncedSearch]);

  useEffect(() => {
    if (mode === 'recap') loadRecapMonths();
    setExpandedRecapMonth(null);
    setRecapDetail({});
  }, [mode, loadRecapMonths]);

  const toggleRecapMonth = async (month) => {
    if (expandedRecapMonth === month) {
      setExpandedRecapMonth(null);
      return;
    }
    setExpandedRecapMonth(month);
    if (!recapDetail[month]) {
      setLoadingRecapDetail(month);
      try {
        const data = await api.getMonthlyRecap({
          month,
          accountId: accountId || undefined,
          merchant: debouncedSearch || undefined,
        });
        setRecapDetail((d) => ({ ...d, [month]: data }));
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingRecapDetail(null);
      }
    }
  };

  // ─── Income ────────────────────────────────────────────────────────────
  useEffect(() => {
    setIncomeData(null);
    setTransfersData(null);
    setLoadingIncome(true);
    setLoadingTransfers(true);
  }, [periodType, selectedMonth, selectedYear]);

  const loadIncome = useCallback(async () => {
    setLoadingIncome(true);
    try {
      const data = periodType === 'year'
        ? await api.getIncomeTransactions({ year: selectedYear })
        : await api.getIncomeTransactions({ month: selectedMonth });
      setIncomeData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingIncome(false);
    }
  }, [periodType, selectedMonth, selectedYear]);

  useEffect(() => { if (mode === 'income') loadIncome(); }, [mode, loadIncome]);

  // ─── Transfers ─────────────────────────────────────────────────────────
  const loadTransfers = useCallback(async () => {
    setLoadingTransfers(true);
    try {
      const data = periodType === 'year'
        ? await api.getTransferTransactions({ year: selectedYear })
        : await api.getTransferTransactions({ month: selectedMonth });
      setTransfersData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingTransfers(false);
    }
  }, [periodType, selectedMonth, selectedYear]);

  useEffect(() => { if (mode === 'transfers') loadTransfers(); }, [mode, loadTransfers]);

  // Client-side search filter for Income/Transfers (backend already returns
  // the full period's data, so this just narrows what's shown)
  const filterBySearch = (items) => {
    if (!debouncedSearch) return items;
    const q = debouncedSearch.toLowerCase();
    return items.filter((i) => i.description.toLowerCase().includes(q));
  };

  const [notTransferRefreshKey, setNotTransferRefreshKey] = useState(0);

  const handleNotTransfer = async (merchantName) => {
    try {
      await api.markNotTransfer(merchantName);
      loadTransfers();
      setNotTransferRefreshKey((k) => k + 1);
      onRefresh?.();
    } catch (err) {
      console.error(err);
    }
  };

  const total = result?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const totalForPage = (result?.transactions || []).reduce(
    (sum, t) => sum + parseFloat(t.amount),
    0
  );

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <h2 style={styles.title}>🧾 All Transactions</h2>
        <div style={styles.modeTabs}>
          {MODES.map((m) => (
            <button
              key={m.id}
              style={{ ...styles.modeTab, ...(mode === m.id ? styles.modeTabActive : {}) }}
              onClick={() => setMode(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Actions - utility functions, not content filters, kept visually quiet */}
      {(onGoToManage || mode === 'list') && (
        <div style={styles.actionsRow}>
          {onGoToManage && (
            <button style={styles.manageLink} onClick={onGoToManage}>
              ✏️ Add/Edit Manual Transactions →
            </button>
          )}
          <div style={styles.actionsRowRight}>
            {mode === 'list' && (
              <button
                style={styles.exportBtn}
                onClick={() => api.exportCsv({ ...getDateRangeForPreset(preset), accountId }).catch(() => {})}
              >
                ⬇ Export CSV
              </button>
            )}
            <button
              style={styles.exportBtn}
              onClick={() => api.exportPdfReport().catch(() => {})}
              title="Download a summary PDF report - budget, net worth, top categories"
            >
              📄 PDF Report
            </button>
          </div>
        </div>
      )}

      {/* Period control - shape depends on mode, but always in the same spot */}
      {mode === 'list' ? (
        <div style={styles.presetRow}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              style={{ ...styles.presetBtn, ...(preset === p.id ? styles.presetBtnActive : {}) }}
              onClick={() => setPreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
      ) : mode === 'recap' ? (
        <div style={styles.presetRow}>
          <span style={styles.recapPeriodLabel}>📅 Last 12 Months</span>
        </div>
      ) : mode === 'top' || mode === 'big' ? null : (
        <div style={styles.presetRow}>
          <div style={styles.periodToggle}>
            <button
              style={{ ...styles.presetBtn, ...(periodType === 'year' ? styles.presetBtnActive : {}) }}
              onClick={() => setPeriodType('year')}
            >
              Year
            </button>
            <button
              style={{ ...styles.presetBtn, ...(periodType === 'month' ? styles.presetBtnActive : {}) }}
              onClick={() => setPeriodType('month')}
            >
              Month
            </button>
          </div>
          {periodType === 'year' ? (
            <select style={styles.yearSelect} value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
              {[0, 1, 2].map((back) => {
                const y = new Date().getFullYear() - back;
                return <option key={y} value={y}>{y}</option>;
              })}
            </select>
          ) : (
            <input
              type="month"
              style={styles.monthPicker}
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            />
          )}
        </div>
      )}

      {/* Filters - refine what's shown in the current view */}
      {mode === 'list' && category && (
        <div style={styles.categoryChipRow}>
          <span style={styles.categoryChip}>
            {categoryIcon(category)} Category: {category}
            <button style={styles.categoryChipClear} onClick={() => setCategory('')} title="Clear category filter">✕</button>
          </span>
        </div>
      )}
      {mode !== 'top' && mode !== 'big' && (
      <div style={styles.controlsRow}>
        <input
          style={styles.search}
          placeholder="Search by merchant..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {mode === 'list' && (
          <div style={styles.sortGroup}>
            <button
              style={{ ...styles.sortBtn, ...(sortBy === 'date' ? styles.sortBtnActive : {}) }}
              onClick={() => setSortBy('date')}
            >
              Date
            </button>
            <button
              style={{ ...styles.sortBtn, ...(sortBy === 'amount' ? styles.sortBtnActive : {}) }}
              onClick={() => setSortBy('amount')}
            >
              Highest $
            </button>
          </div>
        )}
        {mode === 'recap' && (
          <div style={styles.sortGroup}>
            <button
              style={{ ...styles.sortBtn, ...(recapView === 'merchant' ? styles.sortBtnActive : {}) }}
              onClick={() => setRecapView('merchant')}
            >
              By Merchant
            </button>
            <button
              style={{ ...styles.sortBtn, ...(recapView === 'category' ? styles.sortBtnActive : {}) }}
              onClick={() => setRecapView('category')}
            >
              By Category
            </button>
          </div>
        )}
        {mode === 'list' && (
          <select
            style={styles.accountSelect}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">All categories</option>
            {filterCategories.map((c) => (
              <option key={c} value={c}>{categoryIcon(c)} {c}</option>
            ))}
          </select>
        )}
        <select
          style={styles.accountSelect}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
        >
          <option value="">All accounts</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.institution_name} - {a.name}{a.mask ? ` ••${a.mask}` : ''}
            </option>
          ))}
        </select>
      </div>
      )}

      {/* ─── TOP SPENDERS ─── */}
      {mode === 'top' && <TopSpendersView onViewCharges={(merchant) => { setSearch(merchant); setMode('list'); setPreset('all'); }} refreshKey={refreshKey} />}

      {/* ─── BIG PURCHASES ─── */}
      {mode === 'big' && <BigPurchasesView onViewCharges={(merchant) => { setSearch(merchant); setMode('list'); setPreset('all'); }} refreshKey={refreshKey} />}

      {/* ─── LIST ─── */}
      {mode === 'list' && (
        loading && !result ? (
          <p style={styles.empty}>Loading...</p>
        ) : !result?.transactions || result.transactions.length === 0 ? (
          <p style={styles.empty}>No transactions found for this range.</p>
        ) : (
          <>
            <div style={styles.pageSummary}>
              Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              {' · '}
              <span style={{ color: '#e5e7eb' }}>{fmt.currency(totalForPage)}</span> this page
            </div>
            {result.totalAmount !== undefined && (
              <div style={styles.pageSummary}>
                Full range: <span style={{ color: '#e5e7eb' }}>{fmt.currency(result.totalAmount)}</span> total (everything, incl. transfers/payments)
                {' · '}
                <span style={{ color: '#00d4aa' }}>{fmt.currency(result.budgetFilteredAmount)}</span> counts toward budget
              </div>
            )}
            <div style={styles.list}>
              {result.transactions.map((txn) => (
                <TransactionRow key={txn.id} txn={txn} onChanged={() => { loadList(); onRefresh?.(); }} bigPurchaseThreshold={bigPurchaseThreshold} bigPurchaseExcludedCategories={bigPurchaseExcludedCategories} />
              ))}
            </div>
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button style={styles.pageBtn} onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
                  ← Prev
                </button>
                <span style={styles.pageLabel}>Page {page + 1} of {totalPages}</span>
                <button style={styles.pageBtn} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                  Next →
                </button>
              </div>
            )}
          </>
        )
      )}

      {/* ─── RECAP ─── */}
      {mode === 'recap' && (
        loadingRecapMonths ? (
          <p style={styles.empty}>Loading...</p>
        ) : !recapMonths || recapMonths.every((m) => m.count === 0) ? (
          <p style={styles.empty}>No spending found in the last 12 months.</p>
        ) : (
          <div style={styles.list}>
            {recapMonths.filter((m) => m.count > 0).map((m) => {
              const monthName = MONTH_NAMES[parseInt(m.month.split('-')[1], 10) - 1];
              const year = m.month.split('-')[0];
              const isExpanded = expandedRecapMonth === m.month;
              const detail = recapDetail[m.month];
              const isLoadingDetail = loadingRecapDetail === m.month;

              return (
                <div key={m.month} style={styles.monthGroup}>
                  <button style={styles.monthGroupHeader} onClick={() => toggleRecapMonth(m.month)}>
                    <span style={styles.monthGroupTitle}>
                      <span style={styles.monthGroupChevron}>{isExpanded ? '▾' : '▸'}</span>
                      {monthName} {year}
                    </span>
                    <span style={{ ...styles.monthGroupTotal, color: '#e5e7eb' }}>
                      {fmt.currency(m.total)} · {m.count} transaction{m.count !== 1 ? 's' : ''}
                    </span>
                  </button>
                  {isExpanded && (
                    <div style={styles.monthGroupBody}>
                      {isLoadingDetail || !detail ? (
                        <p style={styles.empty}>Loading...</p>
                      ) : recapView === 'category' ? (
                        <CategoryBarChart items={detail.byCategory} totalSpend={detail.totalSpend} />
                      ) : (
                        <>
                          {(() => {
                            const fullList = detail.byMerchant;
                            return fullList.slice(0, 15).map((item, i) => {
                              const label = item.merchant;
                              const pct = detail.totalSpend > 0 ? (parseFloat(item.total) / detail.totalSpend) * 100 : 0;
                              return (
                                <div key={label + i} style={styles.recapRow}>
                                  <div style={styles.recapRank}>{i + 1}</div>
                                  <div style={styles.recapMain}>
                                    <div style={styles.recapTop}>
                                      <span style={styles.recapName}>{label}</span>
                                      <span style={styles.recapAmt}>{fmt.currency(item.total)}</span>
                                    </div>
                                    <div style={styles.recapBarTrack}>
                                      <div style={{ ...styles.recapBarFill, width: `${Math.min(pct, 100)}%` }} />
                                    </div>
                                    <div style={styles.recapMeta}>
                                      {item.count} transaction{item.count !== '1' ? 's' : ''} · {pct.toFixed(0)}% of month
                                      {item.accounts && !accountId && <> · {item.accounts}</>}
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ─── INCOME ─── */}
      {mode === 'income' && (
        loadingIncome || !incomeData || incomeData.mode !== periodType ? (
          <p style={styles.empty}>Loading...</p>
        ) : incomeData.total === 0 ? (
          <p style={styles.empty}>No income found for this period.</p>
        ) : periodType === 'month' ? (
          <IncomeMonthList items={filterBySearch(incomeData.items)} total={incomeData.total} />
        ) : (
          <YearGroupedList
            months={incomeData.months.map((m) => ({ ...m, items: filterBySearch(m.items) }))}
            total={incomeData.total}
            expandedMonths={expandedMonths}
            setExpandedMonths={setExpandedMonths}
            positive
          />
        )
      )}

      {/* ─── TRANSFERS ─── */}
      {mode === 'transfers' && (
        loadingTransfers || !transfersData || transfersData.mode !== periodType ? (
          <p style={styles.empty}>Loading...</p>
        ) : transfersData.total === 0 ? (
          <p style={styles.empty}>No transfers found for this period.</p>
        ) : periodType === 'month' ? (
          <TransferMonthList items={filterBySearch(transfersData.items)} total={transfersData.total} onNotTransfer={handleNotTransfer} />
        ) : (
          <YearGroupedList
            months={transfersData.months.map((m) => ({ ...m, items: filterBySearch(m.items) }))}
            total={transfersData.total}
            expandedMonths={expandedMonths}
            setExpandedMonths={setExpandedMonths}
            onNotTransfer={handleNotTransfer}
          />
        )
      )}

      {/* ─── NOT-A-TRANSFER OVERRIDES ─── */}
      {mode === 'transfers' && <NotTransferList refreshKey={notTransferRefreshKey} onReversed={() => { loadTransfers(); onRefresh?.(); }} />}
    </div>
  );
}

function IncomeMonthList({ items, total }) {
  return (
    <>
      <div style={styles.pageSummary}>
        <span style={{ color: '#00d4aa' }}>{fmt.currency(total)}</span> total income · {items.length} item{items.length !== 1 ? 's' : ''}
      </div>
      <div style={styles.list}>
        {items.map((item) => <IncomeRow key={`${item.type}-${item.id}`} item={item} />)}
      </div>
    </>
  );
}

function TransferMonthList({ items, total, onNotTransfer }) {
  return (
    <>
      <div style={styles.pageSummary}>
        <span style={{ color: '#fbbf24' }}>{fmt.currency(total)}</span> total moved · {items.length} item{items.length !== 1 ? 's' : ''}
      </div>
      <div style={styles.list}>
        {items.map((item) => <TransferRow key={item.id} item={item} onNotTransfer={onNotTransfer} />)}
      </div>
    </>
  );
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function YearGroupedList({ months, total, expandedMonths, setExpandedMonths, positive, onNotTransfer }) {
  const nonEmptyMonths = months.filter((m) => m.items.length > 0);

  return (
    <>
      <div style={styles.pageSummary}>
        <span style={{ color: positive ? '#00d4aa' : '#fbbf24' }}>{fmt.currency(total)}</span> total for the year
      </div>
      {nonEmptyMonths.length === 0 ? (
        <p style={styles.empty}>No matching items this year.</p>
      ) : (
        <div style={styles.list}>
          {nonEmptyMonths.map((m) => {
            const monthName = MONTH_NAMES[parseInt(m.month.split('-')[1], 10) - 1];
            const isExpanded = !!expandedMonths[m.month];
            return (
              <div key={m.month} style={styles.monthGroup}>
                <button
                  style={styles.monthGroupHeader}
                  onClick={() => setExpandedMonths((e) => ({ ...e, [m.month]: !e[m.month] }))}
                >
                  <span style={styles.monthGroupTitle}>
                    <span style={styles.monthGroupChevron}>{isExpanded ? '▾' : '▸'}</span>
                    {monthName}
                  </span>
                  <span style={{ ...styles.monthGroupTotal, color: positive ? '#00d4aa' : '#fbbf24' }}>
                    {fmt.currency(m.total)} · {m.items.length} item{m.items.length !== 1 ? 's' : ''}
                  </span>
                </button>
                {isExpanded && (
                  <div style={styles.monthGroupBody}>
                    {m.items.map((item) => (
                      positive
                        ? <IncomeRow key={`${item.type}-${item.id}`} item={item} />
                        : <TransferRow key={item.id} item={item} onNotTransfer={onNotTransfer} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function IncomeRow({ item }) {
  return (
    <div style={styles.row}>
      <div style={styles.left}>
        <div style={styles.name}>
          {item.description}
          <span style={{
            ...styles.manualTag,
            color: item.type === 'manual' ? '#a78bfa' : '#00d4aa',
            borderColor: item.type === 'manual' ? '#a78bfa' : '#00d4aa',
          }}>
            {item.type === 'manual' ? 'Manual' : 'Bank'}
          </span>
        </div>
        <div style={styles.meta}>
          <span style={styles.date}>{fmt.shortDate(item.date)}</span>
          {item.account && <span style={styles.account}>{item.account}</span>}
          {item.notes && <span style={styles.account}>{item.notes}</span>}
        </div>
      </div>
      <div style={{ ...styles.amount, color: '#00d4aa' }}>+{fmt.currency(item.amount)}</div>
    </div>
  );
}

function TransferRow({ item, onNotTransfer }) {
  const isOut = item.amount > 0;
  return (
    <div style={styles.row}>
      <div style={styles.left}>
        <div style={styles.name}>
          {item.description}
          <span style={{ ...styles.manualTag, color: '#fbbf24', borderColor: '#fbbf24' }}>
            {isOut ? 'Out' : 'In'}
          </span>
        </div>
        <div style={styles.meta}>
          <span style={styles.date}>{fmt.shortDate(item.date)}</span>
          {item.account && <span style={styles.account}>{item.account}</span>}
          {onNotTransfer && (
            <button style={styles.notTransferBtn} onClick={() => onNotTransfer(item.description)}>
              Not a transfer?
            </button>
          )}
        </div>
      </div>
      <div style={{ ...styles.amount, color: isOut ? '#e5e7eb' : '#00d4aa' }}>
        {isOut ? '' : '+'}{fmt.currency(Math.abs(item.amount))}
      </div>
    </div>
  );
}

function NotTransferList({ refreshKey, onReversed }) {
  const [list, setList] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    api.getNotTransferList().then(setList).catch(() => setList([]));
  }, [refreshKey]);

  const reverse = async (merchantName) => {
    try {
      await api.reverseNotTransfer(merchantName);
      setList((l) => l.filter((item) => item.merchant_name !== merchantName));
      onReversed?.();
    } catch (err) {
      console.error(err);
    }
  };

  if (!list || list.length === 0) return null;

  return (
    <div style={styles.notTransferSection}>
      <button style={styles.notTransferHeader} onClick={() => setCollapsed((c) => !c)}>
        <span style={styles.monthGroupTitle}>
          <span style={styles.monthGroupChevron}>{collapsed ? '▸' : '▾'}</span>
          🚫 Marked "Not a Transfer" ({list.length})
        </span>
      </button>
      {!collapsed && (
        <div style={styles.monthGroupBody}>
          <p style={styles.notTransferHelp}>
            These merchants are excluded from automatic transfer detection -
            their transactions show up as regular spending/income instead.
          </p>
          {list.map((item) => (
            <div key={item.merchant_name} style={styles.row}>
              <div style={styles.left}>
                <div style={styles.name}>{item.merchant_name}</div>
                <div style={styles.meta}>
                  <span style={styles.date}>Marked {fmt.shortDate(item.created_at)}</span>
                </div>
              </div>
              <button style={styles.reverseBtn} onClick={() => reverse(item.merchant_name)}>
                ↺ Reverse
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryBarChart({ items, totalSpend }) {
  if (!items || items.length === 0) {
    return <p style={styles.empty}>No spending recorded for this month.</p>;
  }

  const sorted = [...items].sort((a, b) => parseFloat(b.total) - parseFloat(a.total));
  const maxAmt = Math.max(...sorted.map((i) => parseFloat(i.total)));

  return (
    <div style={styles.chartWrap}>
      <div style={styles.chartTotalRow}>
        <span style={styles.chartTotalLabel}>Total spend</span>
        <span style={styles.chartTotalValue}>{fmt.currency(totalSpend)}</span>
      </div>
      <div style={styles.chartRows}>
        {sorted.map((item, i) => {
          const amt = parseFloat(item.total);
          const barPct = maxAmt > 0 ? (amt / maxAmt) * 100 : 0;
          const pctOfTotal = totalSpend > 0 ? (amt / totalSpend) * 100 : 0;
          const color = categoryColor(i);
          // Once the bar gets wide enough, put the dollar amount inside it
          // (white text, right-aligned within the bar); short bars get the
          // amount just after instead so it stays readable
          const labelInsideBar = barPct > 28;

          return (
            <div key={item.category + i} style={styles.chartRow}>
              <div style={styles.chartLabelCol}>
                <span style={styles.chartIcon}>{categoryIcon(item.category)}</span>
                <span style={styles.chartLabel}>{item.category}</span>
              </div>
              <div style={styles.chartBarCol}>
                <div style={styles.chartBarTrack}>
                  <div
                    style={{
                      ...styles.chartBarFill,
                      width: `${Math.max(barPct, 3)}%`,
                      background: `linear-gradient(90deg, ${color}, ${color}cc)`,
                      boxShadow: `0 0 10px ${color}55`,
                    }}
                  >
                    {labelInsideBar && (
                      <span style={styles.chartAmtInside}>{fmt.currency(amt)}</span>
                    )}
                  </div>
                  {!labelInsideBar && (
                    <span style={styles.chartAmtOutside}>{fmt.currency(amt)}</span>
                  )}
                </div>
                <span style={styles.chartPct}>{pctOfTotal.toFixed(0)}% · {item.count} txn{item.count !== '1' ? 's' : ''}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TransactionRow({ txn, onChanged, bigPurchaseThreshold, bigPurchaseExcludedCategories }) {
  const [mode, setMode] = useState(null); // null | 'category' | 'split'
  const name = txn.merchant_name || txn.description || 'Unknown';
  const displayCat = txn.category_override || txn.merchant_override_category || txn.category?.[0] || '';
  const splitCount = parseInt(txn.split_count) || 0;
  const isBigPurchase = bigPurchaseThreshold != null
    && txn.amount >= bigPurchaseThreshold
    && !(bigPurchaseExcludedCategories || []).includes(displayCat);

  return (
    <div style={{ ...styles.txnEntry, ...(isBigPurchase ? styles.txnEntryBig : {}) }}>
      <div style={styles.row}>
        <div style={styles.left}>
          <div style={styles.name}>
            {isBigPurchase && <span style={styles.bigPurchaseTag} title={`Big purchase - $${bigPurchaseThreshold}+`}>💥</span>}
            {name}
            {txn.is_manual && <span style={styles.manualTag}>Manual</span>}
          </div>
          <div style={styles.meta}>
            <span style={styles.date}>{fmt.shortDate(txn.date)}</span>
            {splitCount > 0 ? (
              <button style={styles.splitTagBtn} onClick={() => setMode(mode === 'split' ? null : 'split')}>
                Split ×{splitCount}
              </button>
            ) : (
              <button style={styles.catBtn} onClick={() => setMode(mode === 'category' ? null : 'category')}>
                {displayCat || 'Uncategorized'}
                {txn.category_override && ' ✎'}
                {!txn.category_override && txn.merchant_override_category && ' 🏷'}
              </button>
            )}
            {txn.account_name && (
              <span style={styles.account}>
                {txn.institution_name ? `${txn.institution_name} ${txn.account_name}` : txn.account_name}
              </span>
            )}
            {splitCount === 0 && txn.amount > 0 && (
              <button style={styles.splitLink} onClick={() => setMode(mode === 'split' ? null : 'split')}>Split</button>
            )}
          </div>
        </div>
        <div style={{
          ...styles.amount,
          color: txn.amount < 0 ? '#00d4aa' : '#e5e7eb',
        }}>
          {txn.amount < 0 ? '+' : ''}{fmt.currency(Math.abs(txn.amount))}
        </div>
      </div>

      {mode === 'category' && (
        <CategoryEditor
          txn={txn}
          onDone={() => { setMode(null); onChanged?.(); }}
          onCancel={() => setMode(null)}
        />
      )}
      {mode === 'split' && (
        <SplitEditor
          txn={txn}
          onDone={() => { setMode(null); onChanged?.(); }}
          onCancel={() => setMode(null)}
        />
      )}
    </div>
  );
}

function CategoryEditor({ txn, onDone, onCancel }) {
  const [categories, setCategories] = useState([]);
  const merchantName = txn.merchant_name || txn.description;
  const currentDisplay = txn.category_override || txn.merchant_override_category || txn.category?.[0] || '';
  const [value, setValue] = useState(currentDisplay);
  const [scope, setScope] = useState('transaction'); // 'transaction' | 'merchant'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getCategoriesList().then(setCategories).catch(() => setCategories([]));
  }, []);

  const save = async () => {
    if (!value.trim()) return;
    setSaving(true);
    try {
      await api.recategorizeTransaction(txn.id, value.trim(), scope);
      onDone();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const resetToOriginal = async (resetScope) => {
    setSaving(true);
    try {
      await api.resetTransactionCategory(txn.id, resetScope);
      onDone();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.inlineEditor}>
      <select style={styles.catSelect} value={value} onChange={(e) => setValue(e.target.value)}>
        {!categories.includes(value) && value && <option value={value}>{value}</option>}
        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>

      {merchantName && (
        <div style={styles.scopeChoice}>
          <label style={styles.scopeOption}>
            <input type="radio" checked={scope === 'transaction'} onChange={() => setScope('transaction')} />
            <span>Just this transaction</span>
          </label>
          <label style={styles.scopeOption}>
            <input type="radio" checked={scope === 'merchant'} onChange={() => setScope('merchant')} />
            <span>All transactions from <strong>{merchantName}</strong> (past &amp; future)</span>
          </label>
        </div>
      )}

      <div style={styles.editorActions}>
        <button style={styles.editorSaveBtn} onClick={save} disabled={saving}>Save</button>
        {txn.category_override && (
          <button style={styles.editorResetBtn} onClick={() => resetToOriginal('transaction')} disabled={saving}>
            Reset this transaction
          </button>
        )}
        {txn.merchant_override_category && !txn.category_override && (
          <button style={styles.editorResetBtn} onClick={() => resetToOriginal('merchant')} disabled={saving}>
            Reset vendor rule for {merchantName}
          </button>
        )}
        <button style={styles.editorCancelBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

const fmtPrecise = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

function SplitEditor({ txn, onDone, onCancel }) {
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([
    { category: txn.category_override || txn.category?.[0] || '', amount: '' },
    { category: '', amount: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loadingExisting, setLoadingExisting] = useState((parseInt(txn.split_count) || 0) > 0);

  useEffect(() => {
    api.getCategoriesList().then(setCategories).catch(() => setCategories([]));
  }, []);

  // If this transaction already has splits, load the real ones instead of
  // starting from a blank form - otherwise saving would silently overwrite
  // the existing split with whatever's re-entered from scratch
  useEffect(() => {
    if ((parseInt(txn.split_count) || 0) === 0) return;
    api.getTransactionSplits(txn.id)
      .then((result) => {
        if (result?.splits && result.splits.length > 0) {
          setRows(result.splits.map((s) => ({ category: s.category, amount: String(Math.abs(s.amount)) })));
        }
      })
      .catch(() => {})
      .finally(() => setLoadingExisting(false));
  }, [txn.id, txn.split_count]);

  const total = rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const target = Math.abs(txn.amount);
  const diff = target - total;
  const isValid = Math.abs(diff) <= 0.01;
  const barPct = target > 0 ? Math.min((total / target) * 100, 100) : 0;

  useEffect(() => {
    if (isValid) setError('');
  }, [isValid]);

  const updateRow = (i, field, val) => {
    setRows((r) => r.map((row, idx) => idx === i ? { ...row, [field]: val } : row));
  };

  const addRow = () => setRows((r) => [...r, { category: '', amount: '' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));

  const save = async () => {
    setError('');
    if (!isValid) {
      setError(`Splits must add up to ${fmtPrecise(target)} - currently ${fmtPrecise(total)} (off by ${fmtPrecise(Math.abs(diff))})`);
      return;
    }
    if (rows.some((r) => !r.category.trim() || !r.amount || parseFloat(r.amount) <= 0)) {
      setError('Every split needs a category and a positive amount');
      return;
    }
    setSaving(true);
    try {
      await api.saveTransactionSplits(txn.id, rows.map((r) => ({ category: r.category.trim(), amount: parseFloat(r.amount) })));
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loadingExisting) {
    return <div style={styles.splitCard}><p style={styles.empty}>Loading existing split...</p></div>;
  }

  return (
    <div style={styles.splitCard}>
      <div style={styles.splitCardHeader}>
        <span style={styles.splitCardTitle}>Split Transaction</span>
        <span style={styles.splitCardTarget}>{fmtPrecise(target)}</span>
      </div>

      <div style={styles.splitRowsWrap}>
        <div style={styles.splitColLabels}>
          <span>Category</span>
          <span>Amount</span>
        </div>
        {rows.map((row, i) => (
          <div key={i} style={styles.splitRow}>
            <select
              style={styles.catSelect}
              value={row.category}
              onChange={(e) => updateRow(i, 'category', e.target.value)}
            >
              <option value="">Choose category...</option>
              {!categories.includes(row.category) && row.category && <option value={row.category}>{row.category}</option>}
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={styles.splitAmountWrap}>
              <span style={styles.splitDollarSign}>$</span>
              <input
                type="number"
                step="0.01"
                style={styles.splitAmountInput}
                placeholder="0.00"
                value={row.amount}
                onChange={(e) => updateRow(i, 'amount', e.target.value)}
              />
            </div>
            <button
              style={{ ...styles.removeRowBtn, visibility: rows.length > 2 ? 'visible' : 'hidden' }}
              onClick={() => removeRow(i)}
              title="Remove"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      <button style={styles.addSplitRowBtn} onClick={addRow}>+ Add another category</button>

      <div style={styles.splitProgressSection}>
        <div style={styles.splitProgressTrack}>
          <div style={{
            ...styles.splitProgressFill,
            width: `${barPct}%`,
            background: isValid ? '#00d4aa' : '#fbbf24',
          }} />
        </div>
        <div style={styles.splitProgressLabels}>
          <span style={{ color: isValid ? '#00d4aa' : '#e5e7eb', fontWeight: '700' }}>{fmtPrecise(total)}</span>
          <span style={styles.splitProgressOf}>of {fmtPrecise(target)}</span>
        </div>
      </div>

      {error && <p style={styles.splitError}>{error}</p>}

      <div style={styles.editorActions}>
        <button style={{ ...styles.editorSaveBtn, opacity: isValid ? 1 : 0.5 }} onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save Split'}
        </button>
        <button style={styles.editorCancelBtn} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  modeTabs: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  modeTab: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '999px',
    padding: '5px 14px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  modeTabActive: {
    background: 'rgba(0, 212, 170, 0.12)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  presetRow: {
    display: 'flex',
    gap: '0.4rem',
    marginBottom: '0.75rem',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  presetBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '999px',
    padding: '5px 12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  presetBtnActive: {
    background: 'rgba(0, 212, 170, 0.12)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  periodToggle: { display: 'flex', gap: '0.3rem' },
  yearSelect: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.5rem 0.75rem',
    color: '#fff',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
  },
  monthPicker: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.5rem 0.75rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
  },
  controlsRow: {
    display: 'flex',
    gap: '0.6rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  categoryChipRow: {
    marginBottom: '0.75rem',
  },
  categoryChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'rgba(0, 212, 170, 0.1)',
    border: '1px solid rgba(0, 212, 170, 0.35)',
    borderRadius: '999px',
    padding: '5px 8px 5px 12px',
    color: '#00d4aa',
    fontSize: '0.78rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  categoryChipClear: {
    background: 'rgba(255,255,255,0.08)',
    border: 'none',
    color: '#00d4aa',
    borderRadius: '50%',
    width: '18px',
    height: '18px',
    fontSize: '0.65rem',
    cursor: 'pointer',
    lineHeight: 1,
  },
  search: {
    flex: 1,
    minWidth: '180px',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
    boxSizing: 'border-box',
  },
  sortGroup: { display: 'flex', gap: '0.3rem' },
  sortBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '8px',
    padding: '0.5rem 0.75rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  sortBtnActive: {
    background: 'rgba(0, 212, 170, 0.12)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  actionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
    paddingBottom: '0.75rem',
    marginBottom: '0.75rem',
    borderBottom: '1px solid #1e2a3a',
  },
  actionsRowRight: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  manageLink: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  exportBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '8px',
    padding: '0.55rem 0.9rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  accountSelect: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.5rem 0.6rem',
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
    maxWidth: '220px',
  },
  pageSummary: {
    color: '#6b7280',
    fontSize: '0.75rem',
    fontFamily: "'DM Mono', monospace",
    marginBottom: '0.75rem',
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.875rem',
    textAlign: 'center',
    padding: '2rem 0',
  },
  list: {},
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0',
    borderBottom: '1px solid #1e2a3a',
  },
  left: { flex: 1, minWidth: 0 },
  name: {
    color: '#e5e7eb',
    fontSize: '0.875rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  manualTag: {
    fontSize: '0.6rem',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '4px',
    padding: '1px 5px',
    fontFamily: "'Syne', sans-serif",
    flexShrink: 0,
  },
  meta: {
    display: 'flex',
    gap: '0.4rem',
    alignItems: 'center',
    marginTop: '2px',
    flexWrap: 'wrap',
  },
  date: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  cat: {
    color: '#374151',
    fontSize: '0.65rem',
    background: '#1e2a3a',
    padding: '1px 6px',
    borderRadius: '4px',
    fontFamily: "'Syne', sans-serif",
  },
  txnEntry: {},
  txnEntryBig: {
    borderLeft: '3px solid #ef4444',
    marginLeft: '-1px',
    paddingLeft: '10px',
  },
  bigPurchaseTag: { marginRight: '5px' },
  catBtn: {
    background: '#1e2a3a',
    border: 'none',
    color: '#9ca3af',
    fontSize: '0.65rem',
    padding: '1px 6px',
    borderRadius: '4px',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
  },
  splitTag: {
    color: '#a78bfa',
    fontSize: '0.65rem',
    fontWeight: '700',
    border: '1px solid #a78bfa',
    borderRadius: '4px',
    padding: '1px 6px',
    fontFamily: "'Syne', sans-serif",
  },
  splitTagBtn: {
    background: 'transparent',
    color: '#a78bfa',
    fontSize: '0.65rem',
    fontWeight: '700',
    border: '1px solid #a78bfa',
    borderRadius: '4px',
    padding: '1px 6px',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
  },
  splitLink: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    fontSize: '0.65rem',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  inlineEditor: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '8px',
    padding: '0.7rem 0.85rem',
    marginTop: '0.4rem',
    marginBottom: '0.4rem',
  },
  catSelect: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '5px 8px',
    color: '#fff',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
  },
  scopeChoice: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    margin: '0.6rem 0',
  },
  scopeOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontFamily: "'Syne', sans-serif",
    cursor: 'pointer',
  },
  editorSaveBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 12px',
    fontSize: '0.72rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  editorResetBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '0.72rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  editorCancelBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    borderRadius: '6px',
    padding: '5px 8px',
    fontSize: '0.72rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  editorActions: { display: 'flex', gap: '0.4rem', marginTop: '0.5rem' },
  splitCard: {
    background: 'linear-gradient(135deg, #0d1424, #0a0f1e)',
    border: '1px solid #253247',
    borderRadius: '14px',
    padding: '1.25rem 1.4rem',
    marginTop: '0.6rem',
    marginBottom: '0.6rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
  },
  splitCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '1.1rem',
    paddingBottom: '0.85rem',
    borderBottom: '1px solid #1e2a3a',
  },
  splitCardTitle: {
    color: '#e5e7eb',
    fontSize: '0.95rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
  },
  splitCardTarget: {
    color: '#00d4aa',
    fontSize: '1.15rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  splitRowsWrap: { marginBottom: '0.9rem' },
  splitColLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 40px 0 2px',
    marginBottom: '6px',
  },
  splitRow: {
    display: 'flex',
    gap: '0.6rem',
    alignItems: 'center',
    marginBottom: '0.6rem',
  },
  splitAmountWrap: {
    position: 'relative',
    width: '130px',
    flexShrink: 0,
  },
  splitDollarSign: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6b7280',
    fontSize: '0.85rem',
    pointerEvents: 'none',
  },
  splitAmountInput: {
    width: '100%',
    boxSizing: 'border-box',
    background: '#111827',
    border: '1px solid #253247',
    borderRadius: '8px',
    padding: '10px 12px 10px 26px',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'DM Mono', monospace",
  },
  removeRowBtn: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    cursor: 'pointer',
    fontSize: '0.85rem',
    width: '24px',
    flexShrink: 0,
  },
  addSplitRowBtn: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.78rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    padding: '4px 0',
    marginBottom: '1.1rem',
  },
  splitProgressSection: { marginBottom: '0.9rem' },
  splitProgressTrack: {
    height: '8px',
    background: '#111827',
    borderRadius: '999px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  splitProgressFill: {
    height: '100%',
    borderRadius: '999px',
    transition: 'width 0.3s ease, background 0.3s ease',
  },
  splitProgressLabels: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    fontSize: '0.85rem',
    fontFamily: "'DM Mono', monospace",
  },
  splitProgressOf: {
    color: '#6b7280',
    fontSize: '0.78rem',
  },
  splitError: {
    color: '#ef4444',
    fontSize: '0.75rem',
    lineHeight: '1.5',
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.25)',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    marginBottom: '0.9rem',
  },
  account: {
    color: '#374151',
    fontSize: '0.65rem',
    fontFamily: "'DM Mono', monospace",
  },
  notTransferBtn: {
    background: 'transparent',
    border: 'none',
    color: '#6b7280',
    fontSize: '0.65rem',
    textDecoration: 'underline',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    padding: 0,
  },
  notTransferSection: {
    marginTop: '1.5rem',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  notTransferHeader: {
    width: '100%',
    background: '#0a0f1e',
    border: 'none',
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    textAlign: 'left',
  },
  notTransferHelp: {
    color: '#6b7280',
    fontSize: '0.72rem',
    lineHeight: '1.5',
    marginBottom: '0.75rem',
  },
  reverseBtn: {
    background: 'transparent',
    border: '1px solid #00d4aa',
    color: '#00d4aa',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
    marginLeft: '1rem',
  },
  recapPeriodLabel: {
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  amount: {
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
    marginLeft: '1rem',
    flexShrink: 0,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #1e2a3a',
  },
  pageBtn: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#9ca3af',
    borderRadius: '8px',
    padding: '6px 14px',
    fontSize: '0.8rem',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  pageLabel: {
    color: '#6b7280',
    fontSize: '0.75rem',
    fontFamily: "'DM Mono', monospace",
  },
  chartWrap: {},
  chartTotalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '0.7rem 1rem',
    marginBottom: '1rem',
  },
  chartTotalLabel: {
    color: '#9ca3af',
    fontSize: '0.78rem',
    fontFamily: "'Syne', sans-serif",
  },
  chartTotalValue: {
    color: '#fff',
    fontSize: '1.1rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
  },
  chartRows: { display: 'flex', flexDirection: 'column', gap: '0.65rem' },
  chartRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  chartLabelCol: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '150px',
    flexShrink: 0,
  },
  chartIcon: { fontSize: '0.95rem', flexShrink: 0 },
  chartLabel: {
    color: '#d1d5db',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chartBarCol: { flex: 1, minWidth: 0 },
  chartBarTrack: {
    display: 'flex',
    alignItems: 'center',
    height: '26px',
    position: 'relative',
  },
  chartBarFill: {
    height: '100%',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingRight: '10px',
    transition: 'width 0.5s ease',
    minWidth: '3%',
  },
  chartAmtInside: {
    color: '#0a0f1e',
    fontSize: '0.72rem',
    fontWeight: '800',
    fontFamily: "'DM Mono', monospace",
    whiteSpace: 'nowrap',
  },
  chartAmtOutside: {
    color: '#e5e7eb',
    fontSize: '0.72rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    marginLeft: '8px',
    whiteSpace: 'nowrap',
  },
  chartPct: {
    color: '#4b5563',
    fontSize: '0.65rem',
    fontFamily: "'DM Mono', monospace",
    marginTop: '2px',
    display: 'block',
  },
  recapRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    padding: '0.6rem 0',
    borderBottom: '1px solid #1e2a3a',
  },
  recapRank: {
    color: '#4b5563',
    fontSize: '0.8rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    width: '18px',
    flexShrink: 0,
    paddingTop: '2px',
  },
  recapMain: { flex: 1, minWidth: 0 },
  recapTop: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '4px',
  },
  recapName: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
  },
  recapAmt: {
    color: '#fff',
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  recapBarTrack: {
    height: '5px',
    background: '#0a0f1e',
    borderRadius: '3px',
    overflow: 'hidden',
    marginBottom: '4px',
  },
  recapBarFill: {
    height: '100%',
    background: '#00d4aa',
    borderRadius: '3px',
  },
  recapMeta: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  showMore: {
    display: 'block',
    width: '100%',
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#00d4aa',
    borderRadius: '8px',
    padding: '0.6rem',
    fontSize: '0.8rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    marginTop: '0.75rem',
  },
  monthGroup: {
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    marginBottom: '0.5rem',
    overflow: 'hidden',
  },
  monthGroupHeader: {
    width: '100%',
    background: '#0a0f1e',
    border: 'none',
    padding: '0.75rem 1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  monthGroupTitle: {
    color: '#e5e7eb',
    fontSize: '0.85rem',
    fontWeight: '700',
    fontFamily: "'Syne', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  monthGroupChevron: { color: '#6b7280', fontSize: '0.75rem' },
  monthGroupTotal: {
    fontSize: '0.78rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
  },
  monthGroupBody: {
    padding: '0 1rem',
  },
};
